import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Grid } from '@mui/material';
import { SecurityInventoryCategory } from '@x-guard/xgac-types/xgac';
import { useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { inventoryCategoryIconMap, InventoryCategoryIconName } from '../../../components/icons/inventoryCategoryIcons';
import { AddItemDialog } from './AddItemDialog';

const VISIBLE_CATEGORY_COUNT = 6;
const CATEGORY_TILE_HEIGHT = 74;
const CATEGORY_GRID_ROW_SPACING = 8;

export const CreateItemSection = (props: {
  categories: SecurityInventoryCategory[];
  createItemFlowActive: boolean;
  map: L.Map | null;
  onBeforeCreate: () => void;
  onAfterCreate: () => void;
}) => {

  const translate = useTranslate();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [categoryGridExpanded, setCategoryGridExpanded] = useState(false);

  const selectedCategory = useMemo(() => {

    return props.categories.find((c) => c._id === selectedCategoryId) ?? null;

  }, [props.categories, selectedCategoryId]);

  const handleCategorySelect = (categoryId: string) => {

    setSelectedCategoryId(categoryId);
    setCategoryGridExpanded(true);
    setAddDialogOpen(true);

  };

  const shouldCollapseCategoryGrid = props.categories.length > VISIBLE_CATEGORY_COUNT;
  const categoryGridOpen = categoryGridExpanded || addDialogOpen || props.createItemFlowActive;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1, width: '100%',
    }}>
      <Box
        aria-expanded={shouldCollapseCategoryGrid ? categoryGridOpen : undefined}
        aria-label={shouldCollapseCategoryGrid ? translate('resources.security-inventory-items.text.show_all_categories') : undefined}
        onMouseEnter={() => setCategoryGridExpanded(true)}
        onMouseLeave={() => setCategoryGridExpanded(false)}
        onFocus={() => setCategoryGridExpanded(true)}
        onBlur={(event) => {

          const nextFocusedElement = event.relatedTarget;
          if (nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement)) {

            return;

          }
          setCategoryGridExpanded(false);

        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Collapse
            in={!shouldCollapseCategoryGrid || categoryGridOpen}
            collapsedSize={shouldCollapseCategoryGrid ? CATEGORY_TILE_HEIGHT * 2 + CATEGORY_GRID_ROW_SPACING : 0}
            timeout="auto"
          >
            <Grid container spacing={1}>
              {props.categories.map((category) => {

                const Icon = inventoryCategoryIconMap[category.icon as InventoryCategoryIconName];

                return (
                  <Grid item key={category._id} xs={4}>
                    <button
                      type="button"
                      className="add-device-button"
                      onClick={() => handleCategorySelect(category._id)}
                      style={{
                        height: CATEGORY_TILE_HEIGHT,
                        padding: '8px 6px',
                        justifyContent: 'center',
                        gap: 3,
                      }}
                    >
                      {Icon && <Icon sx={{ fontSize: 22 }} />}
                      <span style={{
                        fontSize: 11,
                        lineHeight: 1.1,
                      }}>
                        {category.name}
                      </span>
                    </button>
                  </Grid>
                );

              })}
            </Grid>
          </Collapse>
          {shouldCollapseCategoryGrid && !categoryGridOpen && (
            <Box sx={{
              backdropFilter: 'blur(2px)',
              background: 'rgba(226, 241, 234, 0.85)',
              bottom: 0,
              height: CATEGORY_TILE_HEIGHT * 0.65,
              left: 0,
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 45%, black 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 45%, black 100%)',
              pointerEvents: 'none',
              position: 'absolute',
              right: 0,
            }}
            />
          )}
        </Box>
        {shouldCollapseCategoryGrid && (
          <Collapse in={!categoryGridOpen} timeout="auto">
            <Box sx={{
              alignItems: 'center',
              display: 'flex',
              fontSize: 12,
              fontWeight: 700,
              justifyContent: 'center',
              mt: 0.5,
            }}>
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
              <span>{translate('resources.security-inventory-items.text.show_all_categories')}</span>
            </Box>
          </Collapse>
        )}
      </Box>
      <AddItemDialog
        open={addDialogOpen}
        category={selectedCategory}
        map={props.map}
        onBeforeCreate={props.onBeforeCreate}
        onAfterCreate={props.onAfterCreate}
        onClose={() => {

          setAddDialogOpen(false);
          setSelectedCategoryId('');

        }}
      />

    </Box>
  );

};
